import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router';
import type { AppConfig } from '@cqp/contracts';
import { api } from './lib/api.js';
import { ConfigProvider, applyBrand } from './lib/config.js';
import { instalarRecuperacaoDeCarga } from './lib/recuperacao.js';
import { LoadingBlock } from './ui/Feedback.js';
import { LimiteDeErro, RecarregarQuandoPuder } from './ui/LimiteDeErro.js';
import { ToastProvider } from './ui/Toast.js';
import './styles.css';

/**
 * Entrada do SPA.
 *
 * O Painel de TV é carregado por `lazy()` **separado** do Painel de Gestão: a TV
 * roda dias numa máquina que não controlamos (Guia §3), e cada quilobyte a menos
 * é uma superfície de falha a menos.
 */
const ManagementApp = lazy(async () => import('./management/ManagementApp.js'));
const TvApp = lazy(async () => import('./tv/TvApp.js'));

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // O SSE invalida o cache; `staleTime` evita refetch redundante entre
      // navegações na mesma sessão.
      staleTime: 10_000,
      retry: (failureCount, error) => {
        // 401/403 não melhoram com repetição: repetir só atrasa o redirecionamento
        // para o login.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

async function boot(): Promise<void> {
  const root = document.getElementById('root');
  if (root === null) throw new Error('elemento #root ausente no index.html');

  /**
   * A configuração do cliente carrega **antes** da primeira renderização.
   *
   * Sem isso a tela pisca com as cores de fallback e só depois assume a marca — e
   * na TV, que fica dias aberta, esse flash apareceria a cada recarregamento.
   */
  let config: AppConfig;
  try {
    config = await api<AppConfig>('/api/config');
  } catch {
    // Sem configuração não há marca, mas há produto: segue com o fallback neutro
    // em vez de deixar a tela branca.
    config = FALLBACK_CONFIG;
  }
  applyBrand(config);
  instalarRecuperacaoDeCarga();

  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={client}>
        <ConfigProvider config={config}>
          <ToastProvider>
            <BrowserRouter>
              {/*
               * O limite de fora pega o que os de dentro não alcançam: a TV ou o painel
               * que nem chegaram a carregar — o pedaço do sistema que sumiu depois de
               * uma publicação. Fundo da marca e recarga assim que der (ADR-0032).
               */}
              <LimiteDeErro
                fallback={
                  <div className="min-h-dvh bg-canvas">
                    <RecarregarQuandoPuder />
                  </div>
                }
              >
                <Suspense fallback={<LoadingBlock />}>
                  <Routes>
                    <Route path="/tv" element={<TvApp />} />
                    <Route path="/*" element={<ManagementApp />} />
                  </Routes>
                </Suspense>
              </LimiteDeErro>
            </BrowserRouter>
          </ToastProvider>
        </ConfigProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

/**
 * O que a tela vira quando `/api/config` **falha**.
 *
 * Genuinamente neutro, e isso é o ponto: a versão anterior dizia "fallback neutro" no
 * comentário e carregava a paleta e os logotipos da Qualimplan aqui dentro — marca da
 * clínica escrita no código do front, que é exatamente o que a Cláusula 7 e o Guia §8
 * proíbem. Vestir outro cliente exigiria editar este arquivo.
 *
 * Cinza e teal genéricos, sem logotipo: a tela fica sóbria e utilizável, e ninguém
 * confunde o estado degradado com a identidade de ninguém. Em operação isto quase
 * nunca aparece — a configuração é buscada antes da primeira renderização, logo só um
 * servidor fora do ar chega aqui.
 */
const FALLBACK_CONFIG: AppConfig = {
  clinicName: 'Painel de Atendimento',
  version: 'desconhecida',
  colors: {
    primary: '#5a6a6e',
    secondary: '#3d5c63',
    secondaryLift: '#47686f',
    secondaryLight: '#8fb0b6',
    neutral: '#75787b',
    detail: '#8a8d8f',
    severityNormal: '#2e7d32',
    severityAttention: '#f9a825',
    severityCritical: '#c62828',
    ink: '#141b1c',
    inkSoft: '#4a4d4f',
    inkMuted: '#6b6e71',
  },
  logo: { default: '', onDark: '' },
  fonts: { display: 'system-ui', text: 'system-ui' },
  features: { writesEnabled: true },
  timeZone: 'America/Sao_Paulo',
  expectedSyncIntervalSeconds: 1800,
};

void boot();
